import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { sectorService, stockService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DotLoader } from 'react-spinners';
import DatePicker, { registerLocale } from 'react-datepicker';
import { vi } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import EarlyBuySignals from './EarlyBuySignals';
import './SmartBoard.css';

registerLocale('vi', vi);

const DEFAULT_INDICES = [
  { symbol: 'VNINDEX', price: null, change_percent: null },
  { symbol: 'VN30', price: null, change_percent: null },
];

const SmartBoard = ({ sectors, setSectors, fetchSectors, stockData, marketIndices, min22Data: min22DataProp, isConnected, socketError, initialLoadDone, earlySignalsData }) => {
  const loading = !initialLoadDone && !Object.keys(stockData).length;
  const [error, setError] = useState(null);
  const { canManageSymbols } = useAuth();

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showCreateSectorModal, setShowCreateSectorModal] = useState(false);
  const [showDeleteSectorModal, setShowDeleteSectorModal] = useState(false);
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [newSymbol, setNewSymbol] = useState('');
  const [newSectorName, setNewSectorName] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // View mode: 'realtime' (default) or 'min22'
  const [viewMode, setViewMode] = useState('realtime');
  const [customMinData, setCustomMinData] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [minLoading, setMinLoading] = useState(false);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Toast state
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }

  // Early signals sidebar
  const [showSignalsPanel, setShowSignalsPanel] = useState(false);

  // min22Data from props
  const min22Data = min22DataProp || {};



  // Native drag & drop — preview reorder approach
  const dragFromRef = useRef(null);      // sector.id being dragged
  const previewRef = useRef(null);       // current reordered array (ref for no stale closures)
  const rafRef = useRef(null);
  const startTimerRef = useRef(null);
  const [draggedId, setDraggedId] = useState(null);
  const [preview, setPreview] = useState(null);

  const displaySectors = preview || sectors;

  const cleanupDrag = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (startTimerRef.current) clearTimeout(startTimerRef.current);
    rafRef.current = null;
    startTimerRef.current = null;
    dragFromRef.current = null;
    previewRef.current = null;
    setDraggedId(null);
    setPreview(null);
  }, []);

  const handleDragStart = useCallback((e, sectorId) => {
    // Set refs immediately so handleDragOver logic works right away
    dragFromRef.current = sectorId;
    previewRef.current = [...sectors];
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');

    // CRITICAL: Delay React state update so the browser can capture the drag image
    // from the original card BEFORE ghost styles (opacity, pointer-events) are applied.
    // Without this delay, the browser captures a ghost-styled element or cancels the drag.
    startTimerRef.current = setTimeout(() => {
      startTimerRef.current = null;
      setDraggedId(sectorId);
      setPreview([...sectors]);
    }, 0);
  }, [sectors]);

  const handleDragOver = useCallback((e, sectorId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Skip if no drag active, or hovering the ghost card
    if (!dragFromRef.current || sectorId === dragFromRef.current) return;
    if (rafRef.current) return;

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const order = previewRef.current;
      if (!order || !dragFromRef.current) return;

      const dragId = dragFromRef.current;
      const fromIdx = order.findIndex(s => s.id === dragId);
      const toIdx = order.findIndex(s => s.id === sectorId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

      const next = [...order];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      previewRef.current = next;
      setPreview(next);
    });
  }, []);

  const handleGridDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    const finalOrder = previewRef.current;
    if (!finalOrder || !dragFromRef.current) {
      cleanupDrag();
      return;
    }

    // Check if order actually changed
    const changed = finalOrder.some((s, i) => s.id !== sectors[i]?.id);
    if (!changed) {
      cleanupDrag();
      return;
    }

    setSectors(finalOrder);
    cleanupDrag();

    // Persist
    try {
      const sectorIds = finalOrder.map(s => s.id);
      await sectorService.reorderSectors(sectorIds);
    } catch (err) {
      console.error('Failed to reorder sectors:', err);
      fetchSectors();
    }
  }, [sectors, setSectors, fetchSectors, cleanupDrag]);

  const handleDragEnd = useCallback(() => {
    cleanupDrag();
  }, [cleanupDrag]);

  // Show toast notification
  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Add symbol to sector
  const handleAddSymbol = async () => {
    if (!newSymbol.trim() || !selectedSector) return;

    setModalLoading(true);
    setModalError('');

    try {
      const response = await sectorService.addSymbol(selectedSector.id, newSymbol.trim());
      // Update sectors with the new data
      setSectors(prev => prev.map(s => s.id === selectedSector.id ? response.sector : s));
      setShowAddModal(false);
      showToast('success', `Đã thêm ${newSymbol.trim()} vào ${selectedSector.name}`);
      setNewSymbol('');
      setSelectedSector(null);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Không thể thêm mã');
    } finally {
      setModalLoading(false);
    }
  };

  // Remove symbol from sector
  const handleRemoveSymbol = async () => {
    if (!selectedSymbol || !selectedSector) return;

    setModalLoading(true);
    setModalError('');

    try {
      const response = await sectorService.removeSymbol(selectedSector.id, selectedSymbol);
      // Update sectors with the new data
      setSectors(prev => prev.map(s => s.id === selectedSector.id ? response.sector : s));
      setShowRemoveModal(false);
      showToast('success', `Đã xóa ${selectedSymbol} khỏi ${selectedSector.name}`);
      setSelectedSymbol(null);
      setSelectedSector(null);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Không thể xóa mã');
    } finally {
      setModalLoading(false);
    }
  };

  // Open add modal
  const openAddModal = (sector) => {
    setSelectedSector(sector);
    setNewSymbol('');
    setModalError('');
    setShowAddModal(true);
  };

  // Open remove modal
  const openRemoveModal = (sector, symbol) => {
    setSelectedSector(sector);
    setSelectedSymbol(symbol);
    setModalError('');
    setShowRemoveModal(true);
  };

  // Open create sector modal
  const openCreateSectorModal = () => {
    setNewSectorName('');
    setModalError('');
    setShowCreateSectorModal(true);
  };

  // Create new sector
  const handleCreateSector = async () => {
    if (!newSectorName.trim()) return;

    setModalLoading(true);
    setModalError('');

    try {
      const response = await sectorService.createSector(newSectorName.trim());
      setSectors(prev => [...prev, response.sector]);
      setShowCreateSectorModal(false);
      showToast('success', `Đã tạo nhóm ngành "${newSectorName.trim()}"`);
      setNewSectorName('');
    } catch (err) {
      setModalError(err.response?.data?.error || 'Không thể tạo nhóm ngành');
    } finally {
      setModalLoading(false);
    }
  };

  // Open delete sector modal
  const openDeleteSectorModal = (sector) => {
    setSelectedSector(sector);
    setModalError('');
    setShowDeleteSectorModal(true);
  };

  // Delete sector
  const handleDeleteSector = async () => {
    if (!selectedSector) return;

    setModalLoading(true);
    setModalError('');
    const sectorName = selectedSector.name;

    try {
      await sectorService.deleteSector(selectedSector.id);
      setSectors(prev => prev.filter(s => s.id !== selectedSector.id));
      setShowDeleteSectorModal(false);
      showToast('success', `Đã xóa nhóm ngành "${sectorName}"`);
      setSelectedSector(null);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Không thể xóa nhóm ngành');
    } finally {
      setModalLoading(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowViewDropdown(false);
      }
    };
    if (showViewDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showViewDropdown]);

  useEffect(() => {
    if (loading) {
      document.body.classList.add('loading-active');
    } else {
      document.body.classList.remove('loading-active');
    }
    return () => {
      document.body.classList.remove('loading-active');
    };
  }, [loading]);

  // Date limits: max = today if after 15:20 VN time, otherwise yesterday
  const now = new Date();
  const vnHour = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Ho_Chi_Minh' }));
  const vnMinutes = vnHour.getHours() * 60 + vnHour.getMinutes();
  const isAfterClose = vnMinutes >= 15 * 60 + 20; // after 15:20
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const maxPickerDate = isAfterClose ? today : yesterday;
  const minDate200 = new Date(today);
  minDate200.setDate(minDate200.getDate() - 300); // 300 calendar days covers ~200 trading days

  // Fetch min range from API when both dates are selected
  useEffect(() => {
    if (!startDate || !endDate) {
      if (!startDate) setCustomMinData(null);
      return;
    }
    const fromStr = startDate.toISOString().split('T')[0];
    const toStr = endDate.toISOString().split('T')[0];
    const controller = new AbortController();
    setMinLoading(true);
    stockService.getMinRange(fromStr, toStr, controller.signal)
      .then(res => {
        if (res && res.data) {
          setCustomMinData(res.data);
        }
        setMinLoading(false);
      })
      .catch(err => {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.error('Error fetching min range:', err);
          setMinLoading(false);
        }
      });
    return () => controller.abort();
  }, [startDate, endDate]);

  // Active min data: custom range or default 22-session
  const activeMinData = customMinData || min22Data;

  const getChangeClass = (changePercent) => {
    if (changePercent == null || changePercent === 0) return 'neutral';
    if (changePercent >= 6.7) return 'ceiling';
    if (changePercent >= 2) return 'positive';
    if (changePercent > 0) return 'positive-muted';
    if (changePercent <= -6.7) return 'floor';
    if (changePercent <= -2) return 'negative';
    return 'negative-muted';
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return price;
  };

  const formatPercent = (percent) => {
    if (percent === null || percent === undefined) return '-';
    const prefix = percent > 0 ? '+' : '';
    return `${prefix}${percent.toFixed(2)}%`;
  };

  const formatVolumeRatio = (ratio) => {
    if (ratio === null || ratio === undefined || ratio === 0) return '-';
    return ratio.toFixed(2);
  };

  const formatVolume = (volume) => {
    if (volume === null || volume === undefined) return '-';
    if (volume >= 1e9) return (volume / 1e9).toFixed(1) + 'B';
    if (volume >= 1e6) return (volume / 1e6).toFixed(1) + 'M';
    if (volume >= 1e3) return (volume / 1e3).toFixed(1) + 'K';
    return volume.toString();
  };

  // Compute advance/decline/unchanged from all sector stocks
  const stockStats = useMemo(() => {
    const allSymbols = new Set();
    sectors.forEach(sector => {
      sector.symbols.forEach(s => allSymbols.add(s));
    });

    let advance = 0;
    let decline = 0;
    let unchanged = 0;

    allSymbols.forEach(symbol => {
      const stock = stockData[symbol];
      if (!stock || stock.change_percent == null) {
        unchanged++;
      } else if (stock.change_percent > 0) {
        advance++;
      } else if (stock.change_percent < 0) {
        decline++;
      } else {
        unchanged++;
      }
    });

    return { advance, decline, unchanged };
  }, [stockData, sectors]);

  return (
    <div className="smart-board-container">
      {loading && (
        <div className="loading-overlay">
          <DotLoader color="#3b82f6" size={50} />
        </div>
      )}

      {/* Fixed Header Section */}
      <div className="fixed-header">
        <div className="smart-board-header">
          <div className="view-mode-dropdown" ref={dropdownRef}>
            <button className="view-mode-btn" onClick={() => setShowViewDropdown(prev => !prev)}>
              <span>{viewMode === 'realtime' ? 'Bảng giá' : 'Min phiên'}</span>
              <svg className={`view-mode-chevron${showViewDropdown ? ' open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            {showViewDropdown && (
              <div className="view-mode-menu">
                <div className={`view-mode-option${viewMode === 'realtime' ? ' active' : ''}`} onMouseDown={() => { setViewMode('realtime'); setShowViewDropdown(false); }}>
                  Bảng giá
                </div>
                <div className={`view-mode-option${viewMode === 'min22' ? ' active' : ''}`} onMouseDown={() => { setViewMode('min22'); setShowViewDropdown(false); }}>
                  Thấp nhất các phiên
                </div>
              </div>
            )}
          </div>
          {viewMode === 'min22' && (
            <div className="min-range-picker">
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                minDate={minDate200}
                maxDate={maxPickerDate}
                monthsShown={2}
                dateFormat="dd/MM/yyyy"
                placeholderText="Chọn khoảng ngày"
                locale="vi"
                className="min-date-input"
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                openToDate={(() => { const d = new Date(maxPickerDate); d.setMonth(d.getMonth() - 1); return d; })()}
                popperPlacement="bottom-start"
              />
              {minLoading && <span className="min-loading-indicator">...</span>}
            </div>
          )}
          <div className="market-indices">
          {(marketIndices.length > 0 ? marketIndices : DEFAULT_INDICES).map((index) => {
            const indexClass = index.change_percent > 0 ? 'positive' : index.change_percent < 0 ? 'negative' : 'neutral';
            const changeValue = index.change != null ? (index.change > 0 ? `+${index.change}` : index.change) : '';
            const changeStr = index.change_percent != null
              ? `${changeValue} (${index.change_percent > 0 ? '+' : ''}${index.change_percent.toFixed(2)}%)`
              : '-';

          return (
            <div key={index.symbol} className={`index-card ${indexClass}`}>
              <div className="index-row-top">
                <span className="index-price">{formatPrice(index.price)}</span>
                <span className="index-change">{changeStr}</span>
              </div>
              <div className="index-row-bottom">
                <span className="index-symbol">{index.symbol}</span>
                <span className="index-volume">{formatVolume(index.volume)}</span>
              </div>
            </div>
          );
        })}
          </div>
          <div className="header-stats">
            <span className="stat-up">▲ {stockStats.advance}</span>
            <span className="stat-flat">■ {stockStats.unchanged}</span>
            <span className="stat-down">▼ {stockStats.decline}</span>
          </div>
        </div>
      </div>

      {/* Scrollable Sectors Grid */}
      <div className="sectors-scroll-container">
        <div
          className={`sectors-grid${draggedId ? ' dragging-active' : ''}`}
          onDragOver={handleGridDragOver}
          onDrop={handleDrop}
        >
          {displaySectors.map((sector) => {
            const isDragged = sector.id === draggedId;
            return (
              <div
                key={sector.id}
                className={`sector-card${isDragged ? ' drag-ghost' : ''}`}
                draggable={canManageSymbols()}
                onDragStart={(e) => handleDragStart(e, sector.id)}
                onDragOver={(e) => handleDragOver(e, sector.id)}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              >
                <div className="sector-header">
                  {canManageSymbols() && (
                    <span className="drag-handle" title="Kéo để sắp xếp">⋮⋮</span>
                  )}
                  {canManageSymbols() && (
                    <button
                      className="sector-add-btn"
                      onClick={(e) => { e.stopPropagation(); openAddModal(sector); }}
                      title="Thêm mã"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </button>
                  )}
                  <h3>{sector.name}</h3>
                  {canManageSymbols() && (
                    <button
                      className="sector-delete-btn"
                      onClick={(e) => { e.stopPropagation(); openDeleteSectorModal(sector); }}
                      title="Xóa nhóm ngành"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  )}
                </div>
                <div className="sector-table">
                  {viewMode === 'realtime' ? (
                  <>
                  <div className="table-header">
                    <span className="col-symbol">Mã</span>
                    <span className="col-price">Giá</span>
                    <span className="col-change">+/-</span>
                    <span className="col-volume">KL</span>
                  </div>
                  <div className="table-body">
                    {[...sector.symbols]
                      .sort((a, b) => {
                        const priceA = stockData[a]?.change_percent || 0;
                        const priceB = stockData[b]?.change_percent || 0;
                        return priceB - priceA;
                      })
                      .map((symbol) => {
                        const stock = stockData[symbol];
                        const changeClass = getChangeClass(stock?.change_percent);

                        return (
                          <div key={symbol} className={`table-row ${changeClass}`}>
                            <span className="col-symbol">{symbol}</span>
                            <span className="col-price">{formatPrice(stock?.price)}</span>
                            <span className={`col-change ${changeClass}`}>
                              {formatPercent(stock?.change_percent)}
                            </span>
                            <span className={`col-volume ${changeClass}`}>
                              {formatVolumeRatio(stock?.percentVolumeMA20)}
                            </span>
                            {canManageSymbols() && (
                              <button
                                className="row-remove-btn"
                                onClick={() => openRemoveModal(sector, symbol)}
                                title="Xóa mã"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              </button>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  </>
                  ) : (
                  <>
                  <div className="table-header">
                    <span className="col-symbol">Mã</span>
                    <span className="col-min22-price">Giá</span>
                    <span className="col-min22">Min</span>
                    <span className="col-min22-pct">%</span>
                  </div>
                  <div className="table-body">
                    {[...sector.symbols]
                      .sort((a, b) => {
                        const pctA = activeMinData[a]?.percent ?? -999;
                        const pctB = activeMinData[b]?.percent ?? -999;
                        return pctB - pctA;
                      })
                      .map((symbol) => {
                        const m = activeMinData[symbol];
                        const stock = stockData[symbol];
                        const pct = m?.percent;
                        const pctClass = pct != null ? (pct <= 3 ? 'neutral' : pct <= 8 ? 'positive-muted' : 'positive') : 'neutral';

                        return (
                          <div key={symbol} className={`table-row ${pctClass}`}>
                            <span className="col-symbol">{symbol}</span>
                            <span className="col-min22-price">{formatPrice(stock?.price)}</span>
                            <span className="col-min22">{m?.min_price != null ? formatPrice(m.min_price) : '-'}</span>
                            <span className={`col-min22-pct ${pctClass}`}>
                              {pct != null ? `${pct.toFixed(2)}%` : '-'}
                            </span>
                            {canManageSymbols() && (
                              <button
                                className="row-remove-btn"
                                onClick={() => openRemoveModal(sector, symbol)}
                                title="Xóa mã"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              </button>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Floating Add Sector Button */}
      {canManageSymbols() && (
        <button className="fab-add-sector" onClick={openCreateSectorModal}>
          <span className="fab-tooltip">Thêm ngành</span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
      )}

      {/* Toast Notification - Connection Error */}
      {(error || socketError) && (
        <div className="toast-container">
          <div className="toast error">
            <span className="toast-message">{error || socketError}</span>
            <button className="toast-action" onClick={() => window.location.reload()}>
              Kết nối lại
            </button>
            <button className="toast-close" onClick={() => setError(null)}>
              ×
            </button>
          </div>
        </div>
      )}

      {/* Toast Notification - Success/Error */}
      {toast && (
        <div className="toast-container">
          <div className={`toast ${toast.type}`}>
            <span className="toast-icon">
              {toast.type === 'success' ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              )}
            </span>
            <span className="toast-message">{toast.message}</span>
            <button className="toast-close" onClick={() => setToast(null)}>
              ×
            </button>
          </div>
        </div>
      )}

      {/* Add Symbol Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Thêm mã vào {selectedSector?.name}</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Nhập mã cổ phiếu (VD: VNM)"
                value={newSymbol}
                onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleAddSymbol()}
                autoFocus
              />
              {modalError && <p className="modal-error">{modalError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowAddModal(false)}>
                Hủy
              </button>
              <button
                className="modal-btn confirm"
                onClick={handleAddSymbol}
                disabled={modalLoading || !newSymbol.trim()}
              >
                {modalLoading ? 'Đang thêm...' : 'Thêm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Symbol Modal */}
      {showRemoveModal && (
        <div className="modal-overlay" onClick={() => setShowRemoveModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Xác nhận xóa</h3>
              <button className="modal-close" onClick={() => setShowRemoveModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Bạn có chắc muốn xóa <strong>{selectedSymbol}</strong> khỏi <strong>{selectedSector?.name}</strong>?</p>
              {modalError && <p className="modal-error">{modalError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowRemoveModal(false)}>
                Hủy
              </button>
              <button
                className="modal-btn danger"
                onClick={handleRemoveSymbol}
                disabled={modalLoading}
              >
                {modalLoading ? 'Đang xóa...' : 'Xóa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Sector Modal */}
      {showCreateSectorModal && (
        <div className="modal-overlay" onClick={() => setShowCreateSectorModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Tạo nhóm ngành mới</h3>
              <button className="modal-close" onClick={() => setShowCreateSectorModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Nhập tên nhóm ngành (VD: NGÂN HÀNG)"
                value={newSectorName}
                onChange={(e) => setNewSectorName(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateSector()}
                autoFocus
              />
              {modalError && <p className="modal-error">{modalError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowCreateSectorModal(false)}>
                Hủy
              </button>
              <button
                className="modal-btn confirm"
                onClick={handleCreateSector}
                disabled={modalLoading || !newSectorName.trim()}
              >
                {modalLoading ? 'Đang tạo...' : 'Tạo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Sector Modal */}
      {showDeleteSectorModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteSectorModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Xác nhận xóa nhóm ngành</h3>
              <button className="modal-close" onClick={() => setShowDeleteSectorModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Bạn có chắc muốn xóa nhóm ngành <strong>{selectedSector?.name}</strong>?</p>
              <p className="modal-warning">Tất cả các mã trong nhóm sẽ bị xóa!</p>
              {modalError && <p className="modal-error">{modalError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowDeleteSectorModal(false)}>
                Hủy
              </button>
              <button
                className="modal-btn danger"
                onClick={handleDeleteSector}
                disabled={modalLoading}
              >
                {modalLoading ? 'Đang xóa...' : 'Xóa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating button for Early Signals */}
      <button
        className={`signals-fab ${showSignalsPanel ? 'active' : ''}`}
        onClick={() => setShowSignalsPanel(!showSignalsPanel)}
        title="Tín hiệu mua sớm"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
      </button>

      {/* Overlay */}
      {showSignalsPanel && (
        <div className="signals-overlay" onClick={() => setShowSignalsPanel(false)} />
      )}

      {/* Right sidebar panel */}
      <div className={`signals-panel ${showSignalsPanel ? 'open' : ''}`}>
        <div className="signals-panel-header">
          <h3>Tín hiệu mua sớm</h3>
          <button className="signals-panel-close" onClick={() => setShowSignalsPanel(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="signals-panel-body">
          {showSignalsPanel && <EarlyBuySignals signalsData={earlySignalsData} />}
        </div>
      </div>
    </div>
  );
};

export default SmartBoard;
